/**
 * Which §D5 kinds draw as tiles and which draw as geometry — spec §F5.2 / §F5.1.
 *
 * A module of its own so `RasterLayers` and `VectorLayers` split the same catalogue by the
 * same predicate: were each to carry its own, a kind added to one list and forgotten in
 * the other would silently draw twice or not at all.
 *
 * Both predicates exclude `unavailable` rows (§D5 / Task 1 — a `wms_external` with no
 * proxy configured). §F5.2 already disables their checkbox, so this is the second of two
 * independent guards, not the only one.
 */
import { useLayoutEffect } from 'react';

import type { Layer, LayerKind } from '../../lib/api';
import { registerRasterLayers } from '../../lib/state';

export const RASTER_KINDS: LayerKind[] = ['cog_titiler', 'wms_external'];
export const VECTOR_KINDS: LayerKind[] = ['points_api', 'lines_api', 'polygons_api'];

export const isRasterKind = (kind: LayerKind): boolean => RASTER_KINDS.includes(kind);

export const isRasterLayer = (layer: Layer): boolean =>
  RASTER_KINDS.includes(layer.kind) && !layer.unavailable;

export const isVectorLayer = (layer: Layer): boolean =>
  VECTOR_KINDS.includes(layer.kind) && !layer.unavailable;

/**
 * Tell the store which catalogue rows are rasters — client review R4's "one raster at a
 * time" invariant (`lib/state.ts`, `registerRasterLayers`).
 *
 * The store cannot ask: the §D5 catalogue is server state and lives in TanStack Query. So
 * whoever already holds that query hands the ids over, and this hook is the single place
 * that does it — `RasterLayers` calls it, because that component is mounted for the whole
 * life of the map and is subscribed to the same `['layers', role]` entry the pane reads, so
 * the register is populated whether or not the reader has ever opened the Layers tab.
 *
 * `unavailable` rows are excluded on purpose: their checkbox is already dead (§F5.2/§D5), so
 * counting them as rasters would let a row nobody can switch on block the rows they can.
 *
 * **A layout effect, not a passive one.** Registering is also what normalises a `layersOn`
 * that was written wholesale — a `#ly=soc,sgphwms` permalink, hydrated before `/layers`
 * existed (`lib/state.ts`, `registerRasterLayers`). Running before paint means the extra
 * raster is dropped in the same frame the catalogue arrives in, so the reader never sees two
 * tile layers stacked, not even for one frame.
 */
export function useRasterRegistry(layers: Layer[] | undefined): void {
  useLayoutEffect(() => {
    if (!layers) return;
    registerRasterLayers(layers.filter(isRasterLayer).map((l) => l.id));
  }, [layers]);
}
